import { FLAG_BRIDGE, FLAG_WATER, RESOURCES, TERRAIN, type ActorMove, type GameEvent, type GameMap, type MapObject, type ResourceKind, type SelfPatch, type SelfState } from '@sunderfen/shared';
import type { WorldView } from './render/world-view.js';
import { LEAF_COLORS, ORE_COL } from './render/world-view.js';
import { snowAt } from './render/weather.js';
import { audio, type Voice } from './audio/engine.js';
import { sfx, type Surface } from './audio/sfx.js';
import { Ambience } from './audio/ambience.js';
import { Music } from './audio/music.js';

/**
 * "Game feel": turns what the server reports into sound and particles.
 * Nothing here changes game state; it only reacts to messages the client already received.
 */
export class Feel {
  readonly ambience: Ambience;
  readonly music = new Music();
  private lx = 0; private lz = 0; private yaw = 0;
  private lastError = 0;

  constructor(private view: WorldView, private map: GameMap, private tickMs: number) {
    this.ambience = new Ambience(map);
  }

  /** Where the listener stands and which way the camera faces. */
  listen(x: number, z: number, yaw: number) { this.lx = x; this.lz = z; this.yaw = yaw; }

  /** Volume and stereo position for a sound at (x, z). Null when too far to hear. */
  at(x: number, z: number, range = 16): Voice | null {
    const dx = x - this.lx, dz = z - this.lz, d = Math.hypot(dx, dz);
    if (d > range) return null;
    const rx = Math.cos(this.yaw), rz = -Math.sin(this.yaw);
    const pan = d < 0.01 ? 0 : ((dx * rx + dz * rz) / d) * Math.min(1, d / 3);
    return { gain: Math.pow(1 - d / range, 1.3), pan };
  }

  private surface(x: number, z: number): Surface {
    const t = this.map.idx(Math.min(this.map.width - 1, Math.max(0, x)), Math.min(this.map.height - 1, Math.max(0, z)));
    if (this.map.flags[t] & FLAG_BRIDGE || this.map.obstacleAt[t] >= 0) return 'wood';
    const ter = this.map.terrain[t];
    if (ter === TERRAIN.path) return 'path';
    if (ter === TERRAIN.sand) return 'sand';
    if (ter === TERRAIN.rock || ter === TERRAIN.snow) return 'rock';
    if (ter === TERRAIN.mud || this.map.regions.find(r => r.id === this.map.region[t])?.key === 'fen') return 'mud';
    return 'grass';
  }

  /** Footprints in soft ground, rings on the water along the shore. */
  private marks(x: number, z: number, face: number, mob?: string) {
    if (Math.hypot(x - this.lx, z - this.lz) > 24) return;
    const t = this.map.idx(x, z), ter = this.map.terrain[t], cx = x + 0.5, cz = z + 0.5;
    const snow = snowAt(this.map, cx, cz);
    const soft = ter === TERRAIN.sand ? 0.6 : ter === TERRAIN.mud || this.map.regions.find(r => r.id === this.map.region[t])?.key === 'fen' ? 0.8 : snow > 0.4 ? 1 : 0;
    if (soft && !(mob && /bat|wraith/.test(mob))) {
      const fx = Math.sin(face) * 0.25, fz = Math.cos(face) * 0.25;
      this.view.traces.footprint(cx - fx, cz - fz, face, -1, soft);
      this.view.traces.footprint(cx + fx, cz + fz, face, 1, soft);
    }
    // walking the waterline: rings spread from the nearest water
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (!this.map.inBounds(x + dx, z + dz) || !(this.map.flags[this.map.idx(x + dx, z + dz)] & FLAG_WATER)) continue;
      if (Math.random() < 0.6) this.view.traces.ripple(cx + dx * 0.7, cz + dz * 0.7);
      break;
    }
  }

  /** An actor moved or swung a tool this tick. `pos` is where it is drawn now. */
  move(mv: ActorMove, isSelf: boolean, pos: { x: number; y: number }, mob?: string) {
    if (mv.jump) { if (isSelf) sfx.teleport(); return; }
    if (mv.path.length) {
      const per = this.tickMs / mv.path.length;
      mv.path.forEach(([x, z], i) => setTimeout(() => {
        const [px, pz] = i ? mv.path[i - 1] : [Math.floor(pos.x), Math.floor(pos.y)];
        this.marks(x, z, Math.atan2(x - px, z - pz), mob);
        const v = this.at(x + 0.5, z + 0.5, 10);
        if (!v) return;
        const s = this.surface(x, z);
        sfx.step(s, { ...v, gain: (v.gain ?? 1) * (isSelf ? 1 : 0.7) });
        if (isSelf && (s === 'path' || s === 'sand') && Math.random() < 0.4) this.view.particles.dust(x + 0.5, this.map.heightAt(x + 0.5, z + 0.5) + 0.05, z + 0.5, s === 'sand' ? 0xe0d2a8 : 0xb89a70);
      }, i * per));
      return;
    }
    // swings, scoops and strikes make their sounds on the impact frame: see impact()
    void mob;
  }

  /**
   * One beat of an action landed: the axe bit, the net dipped, the hammer rang. Called by the
   * actor's animation at the exact frame, so sound, particles and the picture agree.
   * (x, z) is the actor; face is where they look; reach is how far ahead the tool lands.
   */
  impact(beat: string, x: number, z: number, face: number, tool: string | null, mob: string | undefined, isSelf: boolean) {
    const ahead = (d: number) => [x + Math.sin(face) * d, z + Math.cos(face) * d] as const;
    const [fx, fz] = ahead(0.85), v = this.at(fx, fz), y = this.map.heightAt(fx, fz), p = this.view.particles;
    const water = (d: number) => { const [wx, wz] = ahead(d); return [wx, Math.max(0.05, this.map.heightAt(wx, wz) + 0.05), wz] as const; };
    switch (beat) {
      case 'chop': if (v) sfx.chop(v); p.chips(fx, y + 0.9, fz); this.view.strike(x, z, face, 'tree'); break;
      case 'mine': { if (v) sfx.mine(v); const rock = this.view.strike(x, z, face, 'rock'); p.shards(fx, y + 0.4, fz, 0x9a958a); if (rock && ORE_COL[rock.kind]) p.shards(fx, y + 0.45, fz, ORE_COL[rock.kind]); if (Math.random() < 0.5) p.sparks(fx, y + 0.45, fz); break; }
      case 'dip': { if (v) sfx.net(v); const [wx, wy, wz] = water(1.45); p.splash(wx, wy, wz, true); break; }
      case 'lift': { const [wx, wy, wz] = water(1.0); p.splash(wx, wy + 0.6, wz); break; }
      case 'cast': { if (v) sfx.cast(v); const [wx, wy, wz] = water(1.75); p.splash(wx, wy, wz); break; }
      case 'twitch': break;
      case 'reel': { const [wx, wy, wz] = water(1.6); p.splash(wx, wy, wz, true); break; }
      case 'thrust': { if (v) sfx.harpoon(v); const [wx, wy, wz] = water(1.5); p.splash(wx, wy, wz, true); break; }
      case 'trap': { const [wx, wy, wz] = water(1.0); p.splash(wx, wy, wz); break; }
      case 'stir': if (v) sfx.sizzle(v); p.sizzle(fx, y + 0.5, fz); break;
      case 'hammer': if (v) sfx.hammer(v); p.sparks(fx, y + 0.7, fz); break;
      case 'stoke': if (v) sfx.smelt(v); p.embers(fx, y + 0.6, fz); p.smoke(fx, y + 1.2, fz, true); break;
      case 'whittle': if (v) sfx.whittle(v); if (Math.random() < 0.6) p.chips(fx, y + 0.75, fz, [0xe8d2a8, 0xd9b98a]); break;
      case 'pick': if (v) sfx.rustle(v); p.leaves(fx, y + 0.1, fz, [0x6a9a42, 0x8aba52], 3); break;
      case 'heave': if (v) sfx.clear(v); p.dust(fx, y + 0.3, fz, 0xa89a80); break;
      case 'eat': if (isSelf || v) sfx.eat(); break;
      case 'hit': if (!mob && v) sfx.swing(v, !!tool && /battleaxe|maul/.test(tool)); break;
      case 'slam': if (v) sfx.swing(v, true); p.dust(fx, y + 0.1, fz, 0xa89a80); if (isSelf) this.view.shake(0.12); break;
      case 'release': if (v) sfx.bowRelease(v); break;
      case 'thud': { const v2 = this.at(x, z, 14); if (v2) sfx.thud(v2); p.dust(x, this.map.heightAt(x, z) + 0.1, z, 0xa89a80); break; }
    }
  }

  /** A tree was felled or a rock emptied (or came back). */
  resource(o: MapObject, depleted: boolean) {
    if (!depleted) return;
    const [x, y, z] = this.view.objPos(o), v = this.at(x, z, 20), fam = RESOURCES[o.kind as ResourceKind]?.family;
    if (fam === 'tree') { this.view.particles.leaves(x, y + 0.6, z, [0xc8a070, 0xa88050], 8); }
    if (fam === 'rock') { if (v) sfx.rockBreak(v); this.view.particles.shards(x, y + 0.5, z, ORE_COL[o.kind] ?? 0x9a958a); this.view.particles.dust(x, y + 0.3, z, 0x9a958a); if (Math.hypot(x - this.lx, z - this.lz) < 4) this.view.shake(0.04); }
  }

  /** A tree came down nearby. */
  felled(o: MapObject) {
    const [x, y, z] = this.view.objPos(o), v = this.at(x, z, o.kind === 'elder' ? 40 : 20);
    if (v) sfx.treeFall(v);
    // the ground takes the weight when it comes down beside you
    if (Math.hypot(x - this.lx, z - this.lz) < 5) this.view.shake(o.kind === 'elder' ? 0.2 : 0.08);
    this.view.particles.leaves(x, y + 2.2, z, LEAF_COLORS[o.kind] ?? [0x6a9a42], o.kind === 'elder' ? 48 : 18);
  }

  event(e: GameEvent, self: { x: number; y: number; face: number }) {
    const front = { x: self.x + Math.sin(self.face) * 1.2, z: self.y + Math.cos(self.face) * 1.2 };
    switch (e.kind) {
      case 'item': {
        const id = e.item;
        if (id.endsWith('_log')) sfx.logGot();
        else if (id.endsWith('_ore')) sfx.oreGot();
        else if (id.startsWith('raw_')) { /* the 'catch' event plays the sound */ }
        else if (id === 'burnt_fish') sfx.burnt();
        else if (id.startsWith('cooked_')) sfx.cooked();
        else if (id.endsWith('_bar')) sfx.barGot();
        else sfx.pickup();
        break;
      }
      case 'catch':
        if (e.legend) sfx.legend(); else sfx.catchFish();
        if (e.strike) sfx.strike();
        if (e.wr) sfx.record(true); else if (e.pb) sfx.record(false);
        this.view.particles.splash(front.x, 0.05, front.z, true);
        if (e.legend) this.view.particles.sparkle(self.x, this.map.heightAt(self.x, self.y) + 0.2, self.y);
        break;
      case 'bite': sfx.bite(); this.view.particles.splash(self.x + Math.sin(self.face) * 1.75, 0.05, self.y + Math.cos(self.face) * 1.75); break;
      case 'trap': if (e.what === 'set') sfx.trapSet(); else sfx.trapHaul(); this.view.particles.splash(front.x, 0.05, front.z, true); break;
      case 'levelup': { sfx.levelUp(e.level % 10 === 0 || e.level >= 99); const gy = this.map.heightAt(self.x, self.y) + 0.2; this.view.particles.sparkle(self.x, gy, self.y); this.view.particles.fireworks(self.x, gy, self.y, e.level % 10 === 0 || e.level >= 99); break; }
      case 'banner': if (/landmark/i.test(e.eyebrow)) sfx.landmark(); else sfx.discover(); break;
      case 'log': if (e.tone === 'bad' && audio.now - this.lastError > 1) { this.lastError = audio.now; sfx.error(); } break;
    }
  }

  patch(before: SelfState, p: SelfPatch) {
    if (p.coins !== undefined && p.coins > before.coins) sfx.coins();
    if (p.coins !== undefined && p.coins < before.coins) sfx.coins();
    if (p.bank && p.inv && p.inv.filter(Boolean).length < before.inv.filter(Boolean).length) sfx.deposit();
    if (p.fedTicks !== undefined && p.fedTicks > before.fedTicks) sfx.eat();
  }

  station(kind: string) { sfx.open(kind); }
  chat() { sfx.chat(); }

  frame(dt: number, time: number, night: number, rain = 0) {
    this.ambience.rain = rain;
    this.ambience.update(dt, this.lx, this.lz, night, time);
    this.music.mood = { night, region: this.ambience.env.region };
  }
}
